import React, { createContext, useContext, useState, useCallback } from 'react';
import { Modal } from '../components/modal';
import { Button } from '../components/form/button';
import { RenderedContent } from '../utils';
import { cn } from '@workspace/utils';

export interface ModalConfig {
  title?: RenderedContent;
  message?: string;
  children?: React.ReactNode;
  className?: string;
  closeButton?: boolean;
  onClose?: () => void;
}

interface ModalContextType {
  showModal: (config: ModalConfig) => void;
  showError: (messageOrError: string | any, title?: string) => void;
  showSuccess: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  hideModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export interface ModalProviderProps {
  children: React.ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<ModalConfig>({});

  const showModal = useCallback((config: ModalConfig) => {
    setModalConfig(config);
    setIsOpen(true);
  }, []);

  const extractErrorMessage = (error: any): string => {
    if (typeof error === 'string') return error;
    return error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Une erreur est survenue';
  };

  const showError = useCallback((messageOrError: string | any, title: string = 'Attention') => {
    const message = extractErrorMessage(messageOrError);
    showModal({
      title,
      message,
      closeButton: true,
      className: 'max-w-md'
    });
  }, [showModal]);

  const showSuccess = useCallback((message: string, title?: string) => {
    showModal({
      title: title || 'Succès',
      message,
      closeButton: true,
      className: 'max-w-md'
    });
  }, [showModal]);

  const showInfo = useCallback((message: string, title?: string) => {
    showModal({
      title: title || 'Information',
      message,
      closeButton: true,
      className: 'max-w-md'
    });
  }, [showModal]);

  const hideModal = useCallback(() => {
    setIsOpen(false);
    modalConfig.onClose?.();

    // Délai pour laisser la transition se terminer avant de nettoyer le config
    setTimeout(() => {
      setModalConfig({});
    }, 300); // 300ms correspond généralement à la durée de transition du Modal
  }, [modalConfig]);

  return (
    <ModalContext.Provider value={{ showModal, showError, showSuccess, showInfo, hideModal }}>
      {children}
      <Modal
        open={isOpen}
        onClose={hideModal}
        closeButton={false}
        className={cn(modalConfig.className, 'text-center')}
      >
          {modalConfig.title && (
            <h2 className="text-xl font-bold mb-4">{modalConfig.title}</h2>
          )}
          <Modal.Body className="pb-6">
            {modalConfig.message && <p className="text-gray-700">{modalConfig.message}</p>}
            {modalConfig.children}
          </Modal.Body>
          <div className="px-4 pb-6">
            <Button
              onClick={hideModal}
              className="w-full"
              arrow={false}
            >
              J&apos;ai compris !
            </Button>
          </div>
      </Modal>
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}

